import React from 'react';
import { getColor } from 'common/constants/_colors';
import Table from 'components/base/Table';
import Icon from 'components/base/Icon';
import {
  Flex,
  TableNoVerticalSpacesCss
} from 'components/base/layout-components';
import Text from 'components/base/Text';
import Checkbox from 'components/base/Checkbox';
import { verticalScroller } from 'components/base/scroller';
import useOwnerRepositories from './useOwnerRepositories';
import ErrorBox from 'components/InfoBox';
import { StyledWrapperErrorBox } from './OwnerRepositories.style';
import { isTeamLicense } from 'common/utils/utils';
import { LBButton } from '@linearb/buzz';
import { SENSORS_MAP } from '@linearb/linjs-common';

const GITLAB_MAX_LIMIT = 10000;

const ERROR_MESSAGES = {
  NO_ACTIVE_REPOS:
    'No active repos detected. LinearB supports only repositories were active during the last 3 months.',
  NO_PRIVATE_REPOS:
    'No private repos detected. LinearB supports only private repositories.',
  NO_GIT_ORGS:
    "It looks like your Git account doesn't include an organization. You need to have an organization in your Git provider account to proceed."
};

export const OwnerRepositoriesTitle = ({
  numOfSelectedInPage,
  isPageDisabled = false,
  isPageIndeterminate,
  isPageSelected,
  numOfReposInPage,
  numOfFreeSlots,
  rowsPerPage,
  totalReposByOrg,
  isSelectAll,
  toggleSelectAll,
  togglePageSelect,
  isFoundActiveRepos,
  showSelectAll = true
}) => {
  return (
    <Flex items='center' pb='small' justify='space-between' width='100%'>
      <Flex items='center'>
        <Checkbox
          data-cy='OwnerRepositories'
          checked={!isPageDisabled && isPageSelected}
          indeterminate={!isPageSelected && isPageIndeterminate}
          disableRipple={!isSelectAll && numOfFreeSlots <= 0}
          onClick={e => {
            e.preventDefault();
            e.stopPropagation();
            togglePageSelect();
          }}
        />
        <Text size='medium' color='shuttleGray'>
          {numOfSelectedInPage}/{numOfReposInPage} Repos selected
        </Text>
      </Flex>
      {totalReposByOrg > rowsPerPage && (
        <Flex
          pt='x-small'
          pb='x-small'
          pr='x-small'
          pl='small'
          items='center'
          style={
            isSelectAll
              ? {
                  backgroundColor: getColor('zircon'),
                  border: `1px solid ${getColor('fog')}`,
                  borderRadius: '4px'
                }
              : {
                  backgroundColor: 'transparent',
                  border: '1px solid transparent'
                }
          }
        >
          {isSelectAll && (
            <Text pr='0.5rem'>All available repositories selected</Text>
          )}
          {showSelectAll && (
            <LBButton
              data-cy='SelectAll'
              variant='outline'
              size='sm'
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                toggleSelectAll();
              }}
            >
              <Icon
                name={isSelectAll ? 'close' : 'successOutline'}
                size='x-small'
              />
              {isSelectAll ? 'Clear all' : 'Select all available repositories'}
            </LBButton>
          )}
        </Flex>
      )}
    </Flex>
  );
};
const ErrorBoxDiscoveryRepos = ({ text }) => (
  <StyledWrapperErrorBox>
    <ErrorBox
      mt={null}
      icon='erroroutline'
      iconAlign='center'
      iconColor='torchRed'
      backgroundColor='torchRed'
      backgroundAlpha={0.1}
      borderColor='torchRed'
      borderAlpha={0.2}
    >
      <Text size='small'>{text}</Text>
    </ErrorBox>
  </StyledWrapperErrorBox>
);
const getErrorMessage = ({ hasNoGitOrgs, hasNoActiveRepos, isDataExist }) =>
  hasNoGitOrgs
    ? ERROR_MESSAGES.NO_GIT_ORGS
    : !isDataExist
    ? ERROR_MESSAGES.NO_PRIVATE_REPOS
    : hasNoActiveRepos
    ? ERROR_MESSAGES.NO_ACTIVE_REPOS
    : null;
const OwnerRepositories = props => {
  const {
    sensorAuthorization,
    repositories,
    ownerName,
    numOfFreeSlots,
    rowsPerPage,
    page,
    setPage,
    total,
    setLoading,
    isGithubFetchAllRepoTypesFF,
    pageParams,
    hasNoActiveRepos,
    isOnboarding,
    hasNoGitOrgs
  } = props;

  const {
    columns,
    data,
    toggleSelectAll,
    togglePageSelect,
    isSelectAll,
    numOfSelectedInPage,
    totalReposByOrg,
    isPageDisabled,
    isPageSelected,
    isPageIndeterminate,
    isFoundActiveRepos
  } = useOwnerRepositories(props);

  const isDataExist = data.length > 0;
  const errorMessage = getErrorMessage({
    hasNoGitOrgs,
    hasNoActiveRepos,
    isDataExist
  });

  const isGitlab = sensorAuthorization?.sensor_id === SENSORS_MAP.GITLAB;
  const isGitlabMaxLimit = isGitlab && total === GITLAB_MAX_LIMIT;

  return (
    <>
      <OwnerRepositoriesTitle
        organizationName={ownerName}
        numOfSelectedInPage={numOfSelectedInPage()}
        toggleSelectAll={toggleSelectAll}
        togglePageSelect={togglePageSelect}
        isPageDisabled={isPageDisabled()}
        isPageIndeterminate={isPageIndeterminate()}
        isPageSelected={isPageSelected()}
        numOfReposInPage={repositories.length}
        numOfFreeSlots={numOfFreeSlots}
        rowsPerPage={rowsPerPage}
        totalReposByOrg={totalReposByOrg}
        isSelectAll={isSelectAll}
        isFoundActiveRepos={isFoundActiveRepos}
        showSelectAll={!(isOnboarding && isTeamLicense())}
      />
      <Table
        tableWrapperProps={{
          style: {
            flexBasis: '0',
            flexGrow: '1'
          },
          classes: {
            verticalScroller,
            TableNoVerticalSpacesCss
          }
        }}
        onChangePage={(event, nextPage) => {
          setLoading(true);
          setPage(nextPage);
        }}
        page={page}
        rowsPerPageOptions={[]}
        rowsPerPage={rowsPerPage}
        withPagination={isDataExist}
        isPaginationPositionBottom
        totalRows={total}
        columns={columns}
        data={data}
        paginationMessage={
          isGitlabMaxLimit
            ? '* Estimated total due to Gitlab limitation'
            : isGithubFetchAllRepoTypesFF
            ? '* All repositories except archived'
            : '* Private repositories only'
        }
        disableNextPageButton={total === -1 && !pageParams[page]?.next}
        allowEmptyData
        dataCy='repositories-table-add-repo-modal'
      />
      {errorMessage && <ErrorBoxDiscoveryRepos text={errorMessage} />}
    </>
  );
};

export default OwnerRepositories;
